import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, OnDestroy, OnInit, PLATFORM_ID, Renderer2, inject, input } from '@angular/core';

@Directive({
  selector: '[gsPrefersReducedMotion]',
  standalone: true,
})
/**
 * Toggles animation and fallback classes based on the user's
 * `prefers-reduced-motion` media query preference.
 */
export class PrefersReducedMotionDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  /** Distinguishes the server render from the browser. */
  private readonly platformId = inject(PLATFORM_ID);

  // Inputs
  /** Space-separated class list (or array) that represents animated classes to remove for reduced motion. */
  readonly gsPrefersReducedMotion = input<string | string[]>([]);
  /** Space-separated class list (or array) to apply when reduced motion is preferred. */
  readonly gsReducedMotionFallback = input<string | string[]>([]);

  private mediaQuery?: MediaQueryList;
  private boundListener = this.handleMediaChange.bind(this);

  /**
   * Lifecycle: Initialize media query listener and apply initial classes.
   */
  ngOnInit(): void {
    // The server does not know the preference and renders the animations; the browser corrects it on hydration.
    if (!isPlatformBrowser(this.platformId)) {
      this.applyClasses(false);
      return;
    }
    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mediaQuery.addEventListener('change', this.boundListener);
    this.applyClasses(this.mediaQuery.matches);
  }

  /**
   * Lifecycle: Remove media query listener.
   */
  ngOnDestroy(): void {
    this.mediaQuery?.removeEventListener('change', this.boundListener);
  }

  /**
   * Handle media query change event by toggling classes.
   * @param event media query list event
   */
  private handleMediaChange(event: MediaQueryListEvent): void {
    this.applyClasses(event.matches);
  }

  /**
   * Apply or remove animation/fallback classes based on preference.
   * @param prefersReducedMotion whether reduced motion is preferred
   */
  private applyClasses(prefersReducedMotion: boolean): void {
    const element = this.elementRef.nativeElement;

    // Convert inputs to arrays if they're strings
    const gsPrefersReducedMotion = this.gsPrefersReducedMotion();
    const animationClasses = Array.isArray(gsPrefersReducedMotion)
      ? gsPrefersReducedMotion
      : gsPrefersReducedMotion.split(' ').filter(Boolean);

    const gsReducedMotionFallback = this.gsReducedMotionFallback();
    const fallbackClasses = Array.isArray(gsReducedMotionFallback)
      ? gsReducedMotionFallback
      : gsReducedMotionFallback.split(' ').filter(Boolean);

    if (prefersReducedMotion) {
      // Remove animation classes and apply fallback classes
      animationClasses.forEach(className => {
        this.renderer.removeClass(element, className);
      });
      fallbackClasses.forEach(className => {
        this.renderer.addClass(element, className);
      });
    } else {
      // Apply animation classes and remove fallback classes
      animationClasses.forEach(className => {
        this.renderer.addClass(element, className);
      });
      fallbackClasses.forEach(className => {
        this.renderer.removeClass(element, className);
      });
    }
  }
}
